import React, { useCallback, useState } from 'react';
import { toast } from 'react-toastify';

import { Button, Col, Input, Modal, Row, Switch } from 'antd';

import { useEditProductCampaign, useCreateProductCampaign } from 'v2/hooks/campaigns';
import { useUserStore } from 'v2/store/User';

import Coaffiliates from './components/Coaffiliates';

interface IProps {
  campaign?: any;
  product: any;
  handleClose: any;
  onChangeTab?: any;
  producer?: any;
  tabActive?: any;
  updateListCampaigns?: any;
  open: any;
}

const CampaignForm: React.FC<IProps> = ({ campaign: campaignProp, product, handleClose, open }) => {
  const { user } = useUserStore(({ user }) => ({ user }));

  const [isReadyToSave, setIsReadyToSave] = useState(true);
  const [isEditing] = useState(!!campaignProp);
  const [campaign, setCampaign] = useState<any>(() => {
    if (campaignProp) {
      const campaignCopy = JSON.parse(JSON.stringify(campaignProp));
      campaignCopy.product_id = product.id;
      return campaignCopy;
    }

    return {
      active: true,
      title: '',
      product_id: product.id,
      url: '',
      public: false,
      coaffiliates: [
        {
          customer_id: user.id,
          percent: 100,
          name: user.full_name,
          email: user.email
        }
      ]
    };
  });

  const editMutation = useEditProductCampaign();
  const createMutation = useCreateProductCampaign();

  const isBusy = editMutation.isPending || createMutation.isPending;

  const handleChange = useCallback((field: any, e: any) => {
    setCampaign((prev: any) => ({ ...prev, [field]: e.target.value }));
  }, []);

  const handleChangeCheck = useCallback((field: any, e: any) => {
    setCampaign((prev: any) => ({ ...prev, [field]: e.target.checked }));
  }, []);

  const handleChangeCoAffiliates = useCallback((coaffiliates: any) => {
    setCampaign((prev: any) => ({ ...prev, coaffiliates }));
  }, []);

  const handleSubmit = useCallback(async () => {
    let campaignData = { ...campaign };

    if (campaignData.url === '') {
      toast.error('A URL não pode estar vazia');
      return;
    }

    if (campaignData.title === '') {
      toast.error('A Nome da campanha não pode estar vazia');
      return;
    }

    if (!product.is_producer) {
      if (campaignData.coaffiliates.filter((item: any) => item.percent === 0).length > 0) {
        toast.error('Nenhum coafiliado deve ter valor zero');
        return;
      }

      if (campaignData.coaffiliates.filter((item: any) => item.customer_id === 0).length > 0) {
        toast.error('Adicione coafiliados válidos');
        return;
      }

      if (
        campaignData.coaffiliates.reduce((acc: number, item: any) => acc + Number.parseFloat(item.percent), 0) !== 100
      ) {
        toast.error('A soma das porcentagens de coafiliados deve ser 100');
        return;
      }
    } else {
      delete campaignData.coaffiliates;
    }

    campaignData = { ...campaignData, url: campaignData.url.trim() };

    if (isEditing) {
      try {
        delete campaignData.status;
        await editMutation.mutateAsync({ productId: product.id, campaignId: campaignData.id, data: campaignData });
        toast.success('Campanha alterada com sucesso!');
        handleClose(true);
      } catch (error) {
        toast.error(error);
      }
    } else {
      try {
        await createMutation.mutateAsync({ productId: product.id, campaign: campaignData });
        toast.success('Campanha criada com sucesso!');
        handleClose();
      } catch (error) {
        toast.error(error);
      }
    }
  }, [campaign, product, isEditing, handleClose, editMutation, createMutation]);

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title={<span style={{ color: '#192542' }}>{isEditing ? 'Edição de campanha' : 'Cadastro de campanha'}</span>}
      width={780}
      footer={
        <div className='flex justify-end gap-2'>
          <Button id='campaign-form-close' disabled={isBusy} onClick={handleClose}>
            Fechar
          </Button>
          <Button
            id='campaign-form-save'
            type='primary'
            disabled={isBusy || !isReadyToSave}
            onClick={handleSubmit}
            loading={isBusy}
          >
            Salvar
          </Button>
        </div>
      }
    >
      <div>{isEditing ? 'Edite seu funil/campanha' : 'Crie seu funil/campanha'}</div>
      <div className='mt-4' />

      <div className='mb-4 flex items-center gap-2'>
        <Switch
          checked={campaign.active}
          onChange={(checked: boolean) => handleChangeCheck('active', { target: { checked } })}
        />
        <span>{campaign.active ? 'Ativo' : 'Inativo'}</span>
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <label className='mb-1 block text-sm'>Nome da Campanha</label>
          <Input id='campaign-form-title' value={campaign.title} onChange={(e: any) => handleChange('title', e)} />
          <span className='text-xs text-gray-500'>Ex: Campanha de lançamento</span>
        </Col>
        <Col xs={24}>
          <label className='mb-1 block text-sm'>URL de destino</label>
          <Input id='campaign-form-url' value={campaign.url} onChange={(e: any) => handleChange('url', e)} />
          <span className='text-xs text-gray-500'>Ex: https://google.com</span>
        </Col>
        {!product.is_producer && (
          <>
            <Col span={24}>
              <div>Coafiliados</div>
              <div className='mt-1' />

              <Coaffiliates
                coaffiliates={campaign.coaffiliates}
                handleChangeCoAffiliates={handleChangeCoAffiliates}
                public={campaign.public}
                onChangeCheck={handleChangeCheck}
                setReadyToSave={setIsReadyToSave}
              />
            </Col>
          </>
        )}
      </Row>
    </Modal>
  );
};

export default CampaignForm;
